import React, { useMemo, useState, useEffect } from 'react';
import '../Assests/Calendar.css';
import { supabase } from '../Db_Connections/db_connection';

function Calendar({ view = 'Month' }) {
    // --- STATES ---
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
    const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
    const [selectedDate, setSelectedDate] = useState(new Date());

    // Data States
    const [myLeaves, setMyLeaves] = useState([]); // User ki leaves
    const [allLeaves, setAllLeaves] = useState([]); // Admin ke liye sabki leaves
    const [isAdmin, setIsAdmin] = useState(false);

    // Admin Modal State
    const [showAdminModal, setShowAdminModal] = useState(false);
    const [absenteesOnDate, setAbsenteesOnDate] = useState([]);

    // --- FETCH DATA ---
    useEffect(() => {
        const fetchData = async () => {
            const sessionData = sessionStorage.getItem('auth');
            if (!sessionData) return;
            const user = JSON.parse(sessionData);

            // Check Role
            const adminRole = user.role === 'Admin';
            setIsAdmin(adminRole);

            if (adminRole) {
                // ADMIN: Fetch ALL Approved Leaves with User Names
                const { data } = await supabase
                    .from('leaves')
                    .select('*, profiles:profile_id (first_name, last_name, designation)')
                    .eq('status', 'Approved'); // Sirf Approved leaves hi absent mani jayengi
                setAllLeaves(data || []);
            } else {
                // USER: Fetch ONLY MY Approved Leaves
                const { data } = await supabase
                    .from('leaves')
                    .select('*')
                    .eq('profile_id', user.id)
                    .eq('status', 'Approved');
                setMyLeaves(data || []);
            }
        };

        fetchData();
    }, []);

    // --- HELPERS ---
    const monthNames = useMemo(() => ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'], []);
    const weekDays = useMemo(() => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'], []);

    const daysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
    const firstDayOfMonth = (y, m) => new Date(y, m, 1).getDay();

    const formatDateStr = (dateObj) => {
        if (!dateObj) return '';
        // Returns YYYY-MM-DD format to match DB
        const y = dateObj.getFullYear();
        const m = String(dateObj.getMonth() + 1).padStart(2, '0');
        const d = String(dateObj.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    const isSameDate = (a, b) => {
        if (!a || !b) return false;
        return a.toDateString() === b.toDateString();
    };

    // --- LOGIC: CHECK IF ABSENT/ON LEAVE ---
    const checkLeaveStatus = (dateObj) => {
        if (!dateObj) return null;
        const dateStr = formatDateStr(dateObj);

        // Agar Admin hai to check karo 'allLeaves', nahi to 'myLeaves'
        const sourceData = isAdmin ? allLeaves : myLeaves;

        // Check karo agar date kisi leave range ke beech me hai
        const isOnLeave = sourceData.some(leave =>
            dateStr >= leave.start_date && dateStr <= leave.end_date
        );

        return isOnLeave;
    };

    // --- LOGIC: ADMIN CLICK HANDLER ---
    const handleDateClick = (dateObj) => {
        if (!dateObj) return;
        setSelectedDate(dateObj);

        // Agar User hai -> Kuch mat karo (Bas select ho gaya)
        if (!isAdmin) return;

        // Agar Admin hai -> Check karo us din kaun absent hai
        const dateStr = formatDateStr(dateObj);

        const peopleOnLeave = allLeaves.filter(leave =>
            dateStr >= leave.start_date && dateStr <= leave.end_date
        );

        if (peopleOnLeave.length > 0) {
            setAbsenteesOnDate(peopleOnLeave);
            setShowAdminModal(true);
        }
    };

    // --- CALENDAR GRID BUILDER ---
    const buildMonthCells = useMemo(() => {
        const totalDays = daysInMonth(currentYear, currentMonth);
        const firstDay = firstDayOfMonth(currentYear, currentMonth);
        const cells = [];
        for (let i = 0; i < firstDay; i++) cells.push(null);
        for (let day = 1; day <= totalDays; day++) cells.push(new Date(currentYear, currentMonth, day));
        while (cells.length < 42) cells.push(null);
        return cells;
    }, [currentYear, currentMonth]);

    // Week View Helpers
    const getWeekStart = (dateObj) => {
        const d = new Date(dateObj);
        d.setDate(d.getDate() - d.getDay());
        return d;
    };
    const weekDates = useMemo(() => {
        const start = getWeekStart(selectedDate);
        const arr = [];
        for (let i = 0; i < 7; i++) {
            const d = new Date(start);
            d.setDate(start.getDate() + i);
            arr.push(d);
        }
        return arr;
    }, [selectedDate]);

    // Navigation
    const handlePrev = () => {
        if (view === 'Month') setCurrentMonth(prev => prev - 1);
        else setSelectedDate(d => new Date(d.setDate(d.getDate() - (view === 'Week' ? 7 : 1))));
    };
    const handleNext = () => {
        if (view === 'Month') setCurrentMonth(prev => prev + 1);
        else setSelectedDate(d => new Date(d.setDate(d.getDate() + (view === 'Week' ? 7 : 1))));
    };

    // Fix month overflow
    useEffect(() => {
        if (currentMonth < 0) {
            setCurrentMonth(11);
            setCurrentYear(y => y - 1);
        } else if (currentMonth > 11) {
            setCurrentMonth(0);
            setCurrentYear(y => y + 1);
        }
    }, [currentMonth]);

    const headerTitle = useMemo(() => {
        if (view === 'Month') return `${monthNames[currentMonth]} ${currentYear}`;
        if (view === 'Week') {
            const s = weekDates[0];
            const e = weekDates[6];
            return `${s.getDate()} ${monthNames[s.getMonth()]} - ${e.getDate()} ${monthNames[e.getMonth()]}`;
        }
        return `${selectedDate.getDate()} ${monthNames[selectedDate.getMonth()]} ${selectedDate.getFullYear()}`;
    }, [view, monthNames, currentMonth, currentYear, weekDates, selectedDate]);

    return (
        <div className="calendar-view">
            <div className="calendar-header">
                <button type="button" className="nav-btn" onClick={handlePrev}>‹</button>
                <h2>{headerTitle}</h2>
                <button type="button" className="nav-btn" onClick={handleNext}>›</button>
            </div>

            {/* MONTH VIEW */}
            {view === 'Month' && (
                <div className="calendar-grid">
                    {weekDays.map((d) => <div key={d} className="weekday-header">{d}</div>)}

                    {buildMonthCells.map((dateObj, index) => {
                        const empty = !dateObj;
                        const isOnLeave = !empty && checkLeaveStatus(dateObj);
                        const selected = isSameDate(dateObj, selectedDate);

                        return (
                            <div
                                key={index}
                                className={[
                                    'calendar-day',
                                    empty ? 'empty' : '',
                                    isOnLeave ? 'absent' : '', // CSS me 'absent' class red color karegi
                                    selected ? 'selected' : '',
                                ].join(' ')}
                                onClick={() => handleDateClick(dateObj)}
                                style={{ cursor: (!empty && isAdmin && isOnLeave) ? 'pointer' : 'default' }}
                            >
                                {!empty && (
                                    <>
                                        <span className="day-number">{dateObj.getDate()}</span>
                                        {/* User View: Show Absent Label */}
                                        {!isAdmin && isOnLeave && <span className="absent-label">On Leave</span>}
                                        {/* Admin View: Show Dot Indicator */}
                                        {isAdmin && isOnLeave && <div style={{ width: '6px', height: '6px', background: 'red', borderRadius: '50%', margin: '2px auto' }}></div>}
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* WEEK VIEW */}
            {view === 'Week' && (
                <div className="calendar-grid week-grid">
                    {weekDates.map((d) => <div key={`h-${d}`} className="weekday-header">{weekDays[d.getDay()]}</div>)}
                    {weekDates.map((d) => {
                        const isOnLeave = checkLeaveStatus(d);
                        const selected = isSameDate(d, selectedDate);
                        return (
                            <div
                                key={`c-${d}`}
                                className={['calendar-day', isOnLeave ? 'absent' : '', selected ? 'selected' : ''].join(' ')}
                                onClick={() => handleDateClick(d)}
                            >
                                <span className="day-number">{d.getDate()}</span>
                                {!isAdmin && isOnLeave && <span className="absent-label">On Leave</span>}
                                {isAdmin && isOnLeave && <span className="absent-label" style={{ fontSize: '9px' }}>Staff Absent</span>}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* DAY VIEW */}
            {view === 'Day' && (
                <div className="calendar-day-view">
                    <div className="day-card">
                        <h3>{weekDays[selectedDate.getDay()]}, {selectedDate.getDate()} {monthNames[selectedDate.getMonth()]}</h3>
                        {/* Logic for Single Day */}
                        {checkLeaveStatus(selectedDate) ? (
                            <div style={{ color: 'red', marginTop: '10px' }}>
                                <strong>Status: On Leave / Absent</strong>
                                {isAdmin && (
                                    <div style={{ marginTop: '10px', fontSize: '13px', color: '#333' }}>
                                        <p>Absentees:</p>
                                        <ul>
                                            {allLeaves
                                                .filter(l => formatDateStr(selectedDate) >= l.start_date && formatDateStr(selectedDate) <= l.end_date)
                                                .map(l => (
                                                    <li key={l.id}>{l.profiles?.first_name} ({l.leave_type})</li>
                                                ))
                                            }
                                        </ul>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <p style={{ color: 'green', marginTop: '10px' }}>Status: Working Day</p>
                        )}
                    </div>
                </div>
            )}

            {/* ADMIN MODAL: SHOW ABSENTEES */}
            {showAdminModal && isAdmin && (
                <div className="modal-overlay" onClick={() => setShowAdminModal(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
                        <div className="modal-header">
                            <h3>Absentees on {formatDateStr(selectedDate)}</h3>
                            <button className="close-btn" onClick={() => setShowAdminModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            {absenteesOnDate.length === 0 ? (
                                <p>Everyone is present.</p>
                            ) : (
                                <ul style={{ listStyle: 'none', padding: 0 }}>
                                    {absenteesOnDate.map((leave, idx) => (
                                        <li key={idx} style={{ padding: '10px', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <div style={{ width: '30px', height: '30px', background: '#f1f1f1', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 'bold' }}>
                                                {leave.profiles?.first_name?.charAt(0)}
                                            </div>
                                            <div>
                                                <div style={{ fontWeight: 'bold', fontSize: '14px' }}>
                                                    {leave.profiles?.first_name} {leave.profiles?.last_name}
                                                </div>
                                                <div style={{ fontSize: '12px', color: '#666' }}>
                                                    {leave.profiles?.designation} • <span style={{ color: 'red' }}>{leave.leave_type}</span>
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={() => setShowAdminModal(false)}>Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Calendar;